import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardImageCarousel } from "../../../shared/ui/card-image-carousel";
import { StayFromPrice } from "./stay-price";
import { stayNightly } from "../lib/money";
import { SaveHeart } from "../../saved/components/save-heart";
import { photoFrameStyle, photoInnerStyle } from "../../../shared/ui/card-frame";
import type { PublicStayRate } from "../api/stays.api";

import { Icon } from "@majourneys/icons";
export interface StayCardProperty {
  id: string;
  slug: string;
  citySlug: string;
  name: string;
  summary: string;
  starRating: number | null;
  images: string[];
  price: PublicStayRate | null;
  /**
   * Indicative cheapest nightly rate, or null when we have none. Shown only
   * when the guest has picked no dates: a live `price` always wins, because a
   * quote for their own dates beats an average found weeks ago.
   */
  fromPriceNightly?: number | null;
  fromPriceCurrency?: string | null;
}

/**
 * The search this card was rendered for, in exactly the shape the stays routes
 * carry it (`childAges` is the comma separated string from the URL, not an
 * array) so it can be handed to the property link untouched.
 */
export interface StayCardSearch {
  checkin?: string;
  checkout?: string;
  adults?: number;
  /** Comma separated ages, one per child, e.g. "2,15". */
  childAges?: string;
}

export interface StayCardProps {
  property: StayCardProperty;
  /**
   * Whether the guest picked dates for this search. `price: null` means two
   * different things depending on this flag: no dates chosen yet (say
   * nothing), or dates chosen and this property has no rooms for them (say
   * so). The "our supplier is unreachable" case is handled once at the city
   * page level (pricingUnavailable), never per card.
   */
  datesSelected?: boolean;
  /** Active locale, used to build the property detail link. Defaults to "en". */
  lang?: string;
  /**
   * The city as a reader knows it. The property row only carries a slug, and
   * "marrakech" on a card is a URL fragment, not a place, so the name is
   * resolved by the page from the destinations list and handed down.
   */
  cityName?: string;
  /**
   * The dates and party the guest searched with. Carried into the property
   * link so the page they land on prices the same stay this card just quoted:
   * without it the detail page opens undated and asks for dates the guest has
   * already given, and the price they clicked on is gone.
   */
  search?: StayCardSearch;
  /** "Hotel", "Riad"…: the white chip on the photo, as a camp card has "Camp". */
  typeLabel?: string;
}

const POPPINS = "var(--font-sans)";

/**
 * A hotel, drawn as the rest of the site draws a bookable thing.
 *
 * The layout, radii, shadows and CTA are ActivityCardDiscover's, on purpose:
 * a guest coming from /category/camp to /stays/riad is browsing one catalogue
 * as far as they are concerned, and a second card design reads as a second
 * website. What is NOT shared is the price and the availability line. A stay
 * is a total for the dates searched, with taxes that may be due at the
 * property, which is why StayPrice exists and why `datesSelected` is a prop
 * rather than something inferred from a null price.
 */
export function StayCard({
  property,
  datesSelected = false,
  lang = "en",
  cityName,
  search,
  typeLabel,
}: StayCardProps) {
  const { t } = useTranslation();
  const stars = property.starRating ?? 0;
  const to = `/${lang}/stays/${property.citySlug}/${property.slug}`;
  const nights =
    search?.checkin && search?.checkout
      ? Math.max(1, Math.round((Date.parse(`${search.checkout}T00:00:00Z`) - Date.parse(`${search.checkin}T00:00:00Z`)) / 86_400_000))
      : 1;
  /**
   * Only the params that exist. An empty `checkin=` reads to the API as a
   * deliberate blank rather than "not searched", and it would put a dead query
   * string on every undated card link (the landing page renders these too).
   */
  const linkSearch = {
    ...(search?.checkin ? { checkin: search.checkin } : {}),
    ...(search?.checkout ? { checkout: search.checkout } : {}),
    ...(search?.adults != null ? { adults: search.adults } : {}),
    ...(search?.childAges ? { childAges: search.childAges } : {}),
  };

  return (
    // ActivityCardDiscover's shape, so a hotel and a camp sit in one grid as
    // the same card (owner, 2026-09-26): the photo in its own frame, the text
    // on the page beneath it, no panel and no button (owner, 2026-10-06).
    <article
      className="group relative flex h-full w-full flex-col transition-transform duration-200 ease-out hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <div className="mj-photo-frame relative" style={photoFrameStyle}>
      <div className="mj-photo-frame-inner overflow-hidden" style={photoInnerStyle}>
      {/* The photos flip in place; each one is still a router Link, not an
          <a>, so a card click inside a list the guest is browsing does not
          reload the whole document. */}
      <CardImageCarousel
        images={property.images}
        alt={property.name}
        className="aspect-[4/5] w-full overflow-hidden"
        width={480}
        height={600}
        renderSlide={(image, i) => (
          // The photo's alt text names the link, so a gallery of eight does
          // not repeat an aria-label eight times. Only the first is tabbable:
          // the others are the same destination, and a keyboard user should
          // not have to tab past every photo to reach the price.
          <Link
            to={to as never}
            search={linkSearch as never}
            className="block h-full w-full"
            tabIndex={i === 0 ? undefined : -1}
          >
            {image}
          </Link>
        )}
        overlay={
          /* Saved to the account's stay list, the same heart a camp card
             carries in the same spot (owner, 2026-09-29). */
          <SaveHeart
            variant="bare"
            className="absolute end-[4px] top-[4px] z-10"
            target={{
              kind: "stay",
              item: {
                id: property.id,
                slug: property.slug,
                citySlug: property.citySlug,
                name: property.name,
                cityName,
                image: property.images[0],
                starRating: property.starRating ?? undefined,
              },
            }}
          />
        }
      />
      </div>
      </div>

      <div className="flex flex-1 flex-col px-1 pb-[2px] pt-[10px]" style={{ fontFamily: POPPINS }}>
        <h3
          className="m-0 overflow-hidden text-[15.5px] font-bold leading-[1.3]"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={property.name}
        >
          <Link
            to={to as never}
            search={linkSearch as never}
            className="text-[#110E39] no-underline"
          >
            {property.name}
          </Link>
        </h3>

        {/* "Hotel ★★★★★ · Rabat". Stars are shown on stays only (owner rule). */}
        {typeLabel || stars > 0 || cityName ? (
          <p className="m-0 mt-[2px] flex min-w-0 items-center gap-1 text-[13px] leading-[1.35] text-[#5a5d77]">
            {typeLabel ? <span className="shrink-0">{typeLabel}</span> : null}
            {stars > 0 ? (
              <span className="inline-flex shrink-0 gap-px" aria-label={t("stays.star_rating", { count: stars })}>
                {Array.from({ length: stars }).map((_, i) => (
                  <Icon name="ri-star-fill" className="text-[10px] text-[#E5B265]" key={i} aria-hidden="true" />
                ))}
              </span>
            ) : null}
            {cityName ? (
              <span className="min-w-0 break-words">
                {typeLabel || stars > 0 ? "· " : ""}
                {cityName}
              </span>
            ) : null}
          </p>
        ) : null}

        {/* Four states, in strict order of how much they promise:

              1. a live quote for the guest's own dates. Always wins.
              2. dates were searched and this property has nothing. Say so.
              3. no dates, but we know roughly what it costs. Say "from".
              4. no dates and no cached figure. Ask for dates.

            State 3 is an INDICATION from a daily job that prices one sample
            window, which is why it can never outrank state 1 and is worded
            "from". */}
        {property.price ? (
          <StayFromPrice
            inline
            // The same rounding as the page's price block and bottom bar.
            nightly={stayNightly(property.price.sellTotal, nights)}
            currency={property.price.currency}
          />
        ) : !datesSelected && property.fromPriceNightly && property.fromPriceCurrency ? (
          <StayFromPrice
            inline
            nightly={property.fromPriceNightly}
            currency={property.fromPriceCurrency}
          />
        ) : null}

        {/* Taxes the property collects on the day stay off the card (owner,
            2026-10-07): the hotel page and the booking sheet state them on a
            line of their own before the guest pays. */}

        {!property.price && datesSelected ? (
          <p className="m-0 text-[12.5px] leading-tight text-[#5a5d77]">
            {t("stays.no_price_for_dates")}
          </p>
        ) : null}

        {!property.price && !datesSelected && !(property.fromPriceNightly && property.fromPriceCurrency) ? (
          <p className="m-0 text-[12.5px] leading-tight text-[#5a5d77]">
            {t("stays.pick_dates_for_prices")}
          </p>
        ) : null}
      </div>
    </article>
  );
}
